import { Component, computed, effect, inject, output, signal, Signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingIndicatorComponent } from 'src/app/shared/components/loading-indicator/loading-indicator.component';
import { select, Store } from '@ngxs/store';
import { SearchState } from 'src/app/search/state/search.state';
import { ISearchResult, SEARCH_RESULT_TYPE } from 'src/app/shared/interfaces/search.interface';
import { debounceTime, tap, first, finalize, filter } from 'rxjs';
import { SearchByTerm } from 'src/app/search/state/search.actions';
import { Router } from '@angular/router';
import { SetFaqSearchTerm } from 'src/app/faq/state/faq.actions';
import { HelpItemService } from 'src/app/shared/services/help-item.service';
import { CategoryEnum } from 'src/app/shared/enums/CategoryEnum';
import { TopicsTileInterface } from 'src/app/shared/interfaces/topics-tile.interface';
import { CountryCode } from 'src/app/shared/interfaces/IHelpItemCountryAddOn';
import { CountryEnum } from 'src/app/shared/enums/CountryEnum';
import { HeaderSearchLinkComponent } from './header-search-link/header-search-link.component';
import { HeaderSearchItemComponent } from './header-search-item/header-search-item.component';

@Component({
  selector: 'app-header-search-results',
  imports: [TranslateModule, HeaderSearchItemComponent, LoadingIndicatorComponent, HeaderSearchLinkComponent],
  templateUrl: './header-search-results.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header-search-results.component.scss'
})
export class HeaderSearchResultsComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly helpItemService = inject(HelpItemService);

  public closeSearchResults = output<void>();

  protected readonly results: Signal<ISearchResult[]> = select(SearchState.results);
  protected readonly hasMoreResults: Signal<boolean> = select(SearchState.hasMoreResults);
  protected readonly searchTerm: Signal<string> = select(SearchState.searchTerm);
  protected readonly isSearching: Signal<boolean> = select(SearchState.isSearching);

  protected filteredResults = computed(() => this.results().filter(results => results.type !== SEARCH_RESULT_TYPE.FAQ));
  protected hasFaqResult = computed(() => this.results().some(results => results.type === SEARCH_RESULT_TYPE.FAQ));

  protected countryFilter: Signal<CountryCode[]> = computed(() => {
    const searchTerm = this.searchTerm().toLowerCase().trim();

    if (!searchTerm) {
      return [];
    }

    const searchTermParts = searchTerm.split(' ').filter(part => part.trim());
    const countryNameToCountryCode = new Map<string, CountryCode>();
    const countryNames = new Set(Object.values(CountryEnum).map(name => name.toLowerCase()));
    const countryCodes = new Set(Object.keys(CountryEnum) as CountryCode[]);
    const matchingCodes: CountryCode[] = [];

    (Object.keys(CountryEnum) as CountryCode[]).forEach(code => {
      countryNameToCountryCode.set(code.toLowerCase(), code);
    });

    Object.entries(CountryEnum).forEach(([code, name]) => {
      countryNameToCountryCode.set(name.toLowerCase(), code as CountryCode);
    });

    countryNameToCountryCode.forEach((code, nameOrCode) => {
      if (countryNames.has(nameOrCode) && searchTerm.includes(nameOrCode)) {
        matchingCodes.push(code);
      } else if (countryCodes.has(code) && searchTermParts.includes(nameOrCode)) {
        matchingCodes.push(code);
      }
    });

    return [...new Set(matchingCodes)];
  });

  private scrolledToBottom: WritableSignal<boolean> = signal<boolean>(false);

  protected isAutoLoading: WritableSignal<boolean> = signal<boolean>(false);

  constructor() {
    // Auto-load until we have at least 5 visible items
    // Because FAQ items are filtered out, we may need to load more than once to make scrolling possible
    effect(() => {
      const visibleCount = this.filteredResults().length;
      const hasMore = this.hasMoreResults();
      const isSearching = this.isSearching();
      const currentlyAutoLoading = this.isAutoLoading();

      // Don't start new auto-load if one is already running
      if (!isSearching && !currentlyAutoLoading && visibleCount < 5 && hasMore) {
        this.isAutoLoading.set(true);
        setTimeout(() => this.loadMore(true), 200);
      }
    });
  }

  protected onDropdownScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 5;

    if (this.isSearching() || this.isAutoLoading() || this.scrolledToBottom()) {
      return;
    }

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      this.onScrollToBottom();
    }
  }

  protected onClickFaqNotice(): void {
    this.store.dispatch(new SetFaqSearchTerm(this.searchTerm())).pipe(first()).subscribe();
    void this.router.navigate(['/faq/all']);
    this.closeSearchResults.emit();
  }

  protected onClickCategory(result: ISearchResult): void {
    this.helpItemService
      .fetchHelpTiles()
      .pipe(
        filter((tiles: TopicsTileInterface[]) => tiles.some(tile => (tile.title as CategoryEnum) === result.entry.category)),
        tap(tiles => {
          const category = tiles.find(tile => (tile.title as CategoryEnum) === result.entry.category);
          void this.router.navigate([category.url]);
          this.closeSearchResults.emit();
        }),
        first()
      )
      .subscribe();
  }

  private onScrollToBottom(): void {
    this.scrolledToBottom.set(true);
    this.loadMore(false);
  }

  private loadMore(isAutoLoad: boolean = false): void {
    this.store
      .dispatch(new SearchByTerm())
      .pipe(
        debounceTime(500),
        tap(() => {
          this.scrolledToBottom.set(false);
        }),
        finalize(() => {
          if (isAutoLoad) {
            this.isAutoLoading.set(false);
          }
        }),
        first()
      )
      .subscribe();
  }
}
